import {
  AbilityBuilder,
  createMongoAbility,
  type MongoAbility,
} from '@casl/ability';

import { isAdmin } from '@client/utils/auth.util';

import type { EUserRole, TUserRole } from '@common/types';
import type { User } from 'firebase/auth';

enum EAbilityActions {
  READ = 'read',
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  ACTIVATE = 'activate',
  DEACTIVATE = 'deactivate',
  MANAGE = 'manage',
  PREVIEW = 'preview',
  EXPORT = 'export',
  SUBSCRIBE = 'subscribe',
  UNSUBSCRIBE = 'unsubscribe',
  ASSIGN = 'assign',
  UNASSIGN = 'unassign',
  CANCEL = 'cancel',
  PAY = 'pay',
}

enum EAbilitySubjects {
  HOME = 'home',
  LOGIN = 'login',
  LOGOUT = 'logout',
  PROFILE = 'profile',
  TENANTS = 'clients',
  TENANT = 'client',
  DASHBOARD = 'dashboard',
  SUBSCRIPTIONS = 'subscriptions',
  SUBSCRIPTION = 'subscription',
  SCHEDULER = 'scheduler',
  FEATURES = 'features',
  FEATURE = 'feature',
  TRIAL = 'trial',
  DISCOUNT = 'discount',
  PERIODIC = 'periodic',
  TAGS = 'tags',
  MARKETPLACE = 'marketplace',
  CREDITS = 'credits',
  REVENUE = 'revenue',
  ENTITLEMENTS = 'entitlements',
  ALL = 'all',
}

type TAbility = MongoAbility<[EAbilityActions, string]>;

/**
 * Defines the ability for a user based on their role.
 * @param {user: User | null, role: EUserRole | null} props - The parameters object.
 * @param {User | null} props.user - The user object.
 * @param {EUserRole} props.role - The role of the user.
 * @returns {TAbility} The defined ability for the user.
 */
function defineAbilityFor({
  user,
  role,
}: {
  user: User | null;
  role: EUserRole | null;
}): TAbility {
  const { can, cannot, build } = new AbilityBuilder<TAbility>(
    createMongoAbility,
  );

  if (user) {
    if (isAdmin(role as TUserRole)) {
      updateAbility(can, 'MANAGE', ['ALL']);
    } else {
      updateAbility(can, 'MANAGE', [user?.uid]);
      updateAbility(can, 'READ', ['DASHBOARD']);

      updateAbility(can, 'READ', ['TENANTS']);
      updateAbility(can, 'EXPORT', ['TENANTS']);
      updateAbility(can, 'MANAGE', ['TENANT']);

      updateAbility(can, 'READ', ['SUBSCRIPTIONS']);
      updateAbility(can, 'EXPORT', ['SUBSCRIPTIONS']);
      updateAbility(can, 'MANAGE', ['SUBSCRIPTION']);
      updateAbility(can, 'ASSIGN', ['SUBSCRIPTION', 'FEATURES']);
      updateAbility(can, 'MANAGE', ['SUBSCRIPTION', 'SCHEDULER']);

      updateAbility(can, 'READ', ['FEATURES']);
      updateAbility(can, 'EXPORT', ['FEATURES']);
      updateAbility(can, 'MANAGE', ['FEATURE']);
      updateAbility(can, 'ASSIGN', ['FEATURE', 'SUBSCRIPTIONS']);
      updateAbility(can, 'DEACTIVATE', ['FEATURE']);
      updateAbility(can, 'UPDATE', ['FEATURE', 'TRIAL']);
      updateAbility(can, 'UPDATE', ['FEATURE', 'DISCOUNT']);
      updateAbility(can, 'UPDATE', ['FEATURE', 'PERIODIC']);
      updateAbility(can, 'MANAGE', ['FEATURE', 'SCHEDULER']);

      updateAbility(can, 'MANAGE', ['TAGS']);

      updateAbility(can, 'READ', ['CREDITS']);
      updateAbility(can, 'READ', ['REVENUE']);
      updateAbility(can, 'READ', ['ENTITLEMENTS']);
    }

    updateAbility(cannot, 'READ', ['LOGIN']);
    updateAbility(can, 'READ', ['LOGOUT']);
  } else {
    updateAbility(can, 'READ', ['LOGIN']);
    updateAbility(cannot, 'READ', ['LOGOUT']);
  }

  updateAbility(can, 'READ', ['HOME']);

  return build();
}

// eslint-disable-next-line react-refresh/only-export-components
export { defineAbilityFor, EAbilityActions, EAbilitySubjects };

const updateAbility = (
  rule: AbilityBuilder<TAbility>['can'] | AbilityBuilder<TAbility>['cannot'],
  action: keyof typeof EAbilityActions,
  subjects: (keyof typeof EAbilitySubjects)[] | string[] = [],
) => {
  rule(
    [EAbilityActions[action]],
    subjects
      .map((subject: keyof typeof EAbilitySubjects | string) => {
        if (Object.prototype.hasOwnProperty.call(EAbilitySubjects, subject)) {
          // Use the enum value if valid
          return EAbilitySubjects[subject as keyof typeof EAbilitySubjects];
        } else {
          // Use raw string if not in enum
          return subject;
        }
      })
      .join('.'),
  );
};
